<template>
  <Modal v-if="isOpen" backdrop-blur>
    <div
      class="org-onboarding-modal bg-white rounded-2xl flex flex-col shadow-lg overflow-hidden"
      :style="modalStyle"
      @click.stop
    >
      <!-- Stepper -->
      <div class="px-6 pt-6 pb-0 border-b border-gray-100">
        <div class="flex w-full">
          <div
            v-for="(step, idx) in steps"
            :key="step.id"
            class="step-tab flex-1 flex flex-col items-center pb-3"
            :class="{ 'step-tab--active': currentStep === idx + 1 }"
          >
            <div class="flex items-center gap-2">
                <span
                  class="step-circle flex items-center justify-center text-sm font-semibold mb-2"
                  :class="
                    currentStep === idx + 1
                      ? 'step-circle--active text-white'
                      : 'step-circle--inactive text-text-romanSilver'
                  "
                >
                  {{ idx + 1 }}
                </span>
                <span
                  class="text-xs font-semibold text-center px-1"
                  style="color: #495057;"
                >
                  {{ step.label }}
                </span>
            </div>
            <span
              class="step-underline mt-2 h-1 w-full rounded-full transition-colors"
              :class="
                currentStep === idx + 1 ? 'step-underline--active' : 'bg-transparent'
              "
            />
          </div>
        </div>
        <div
          class="inline-block px-3 py-1 rounded-full font-black mb-4 mt-2"
          style="background: #F7F7F7; font-size: 10px;"
        >
          STEP {{ currentStep }} OF {{ steps.length }}
        </div>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-5 min-h-0">
        <OrgOnboardingStep1
          v-if="currentStep === 1"
          :selections="selections"
          :job-level-disabled="jobLevelDisabled"
          :job-grade-disabled="jobGradeDisabled"
          :grade-level-disabled="gradeLevelDisabled"
          :steps-checkbox-disabled="stepsCheckboxDisabled"
          :range-based-disabled="rangeBasedDisabled"
          :scale-based-disabled="scaleBasedDisabled"
          @option-toggle="onStep1OptionToggle"
        />

        <OrgOnboardingStep2
          v-else-if="currentStep === 2"
          :review-rows="reviewRows"
          :review-confirmed="reviewConfirmed"
          @toggle="reviewConfirmed = $event"
        />

        <OrgOnboardingStep3
          v-else
          :module-queue-rows="moduleQueueRows"
        />
      </div>

      <!-- Footer -->
      <div
        class="flex items-center gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0"
      >
        <button
          v-if="currentStep === 1"
          type="button"
          class="px-4 py-2 rounded-lg text-sm font-semibold text-flame border-2 border-flame"
          @click="goToAdminDashboard"
        >
          Cancel
        </button>
        <button
          v-if="currentStep > 1 && currentStep < steps.length"
          type="button"
          class="px-4 py-2 rounded-lg text-sm font-semibold text-flame border-2 border-flame"
          @click="currentStep -= 1"
        >
          Back
        </button>
        <button
          v-if="currentStep < steps.length"
          type="button"
          class="px-5 py-2.5 ml-auto rounded-lg text-sm font-bold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
          :class="{ 'opacity-50 cursor-not-allowed': isContinueDisabled }"
          :disabled="isContinueDisabled"
          style="background: var(--org-onboarding-accent, #f06431)"
          @click="handlePrimaryStep"
        >
          {{ currentStep === 1 ? "Review selection" : "Complete setup" }}
        </button>
        <button
          v-else
          type="button"
          class="px-5 py-2.5 rounded-lg ml-auto text-sm font-bold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
          style="background: var(--org-onboarding-accent, #f06431)"
          @click="finishOnboarding"
        >
          Start setup
        </button>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapState } from "vuex";
import Modal from "@/components/Modal";
import OrgOnboardingStep1 from "./OrgOnboardingStep1";
import OrgOnboardingStep2 from "./OrgOnboardingStep2";
import OrgOnboardingStep3 from "./OrgOnboardingStep3";

export default {
  name: "OrgOnboarding",
  components: { Modal, OrgOnboardingStep1, OrgOnboardingStep2, OrgOnboardingStep3 },
  props: {
    value: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentStep: 1,
      steps: [
        { id: "hr", label: "HR architecture" },
        { id: "review", label: "Review & confirm" },
        { id: "queue", label: "Module queue" },
      ],
      selections: {
        jobFamily: false,
        jobCadre: false,
        schemeOfService: false,
        jobLevel: false,
        jobGrade: false,
        gradeLevel: false,
        steps: false,
        rangeBasedCompensation: false,
        scaleBasedCompensation: false,
      },
      /** Shown on step 2 review; replace when org type is loaded from context. */
      organisationType: "",
      reviewConfirmed: false,
      savingOrgSetup: false,
      savedSetupPayload: null,
    };
  },
  computed: {
    ...mapState({
      appearanceBtn: (state) => state.orgAppearance.orgAppearanceData?.buttonColor,
      defaultBtn: (state) => state.orgAppearance.defaultBtnColor,
    }),
    isOpen() {
      return this.value;
    },
    accentColor() {
      return this.appearanceBtn || this.defaultBtn || "#f06431";
    },
    modalStyle() {
      return {
        "--org-onboarding-accent": this.accentColor,
        maxWidth: "640px",
        width: "calc(100vw - 32px)",
        maxHeight: "min(90vh, 720px)",
      };
    },
    jobLevelDisabled() {
      return this.selections.jobFamily || this.selections.jobCadre;
    },
    jobGradeDisabled() {
      if (this.selections.jobCadre) {
        return true;
      }
      if (this.selections.jobFamily) {
        return false;
      }
      return !this.selections.rangeBasedCompensation;
    },
    gradeLevelDisabled() {
      return (
        this.selections.jobFamily ||
        this.selections.jobCadre ||
        this.selections.jobLevel ||
        this.selections.jobGrade
      );
    },
    /** Steps follow grade level: on when GL is on; checkbox is read-only. */
    stepsCheckboxDisabled() {
      return true;
    },
    rangeBasedDisabled() {
      if (this.selections.jobCadre) {
        return true;
      }
      return !this.selections.jobLevel;
    },
    scaleBasedDisabled() {
      return this.selections.jobCadre;
    },
    reviewJobClassificationDisplay() {
      const parts = [];
      if (this.selections.jobFamily) parts.push("Job families");
      if (this.selections.jobCadre) parts.push("Job cadres");
      if (this.selections.schemeOfService) parts.push("Scheme of service");
      return parts.length ? `${parts.join(" · ")}.` : "None selected.";
    },
    reviewLevelsDisplay() {
      const parts = [];
      if (this.selections.jobLevel) parts.push("Job levels");
      if (this.selections.jobGrade) parts.push("Job grades");
      if (this.selections.gradeLevel) parts.push("Grade levels");
      if (this.selections.steps) parts.push("Steps");
      return parts.length ? `${parts.join(" · ")}.` : "None selected.";
    },
    reviewCompensationDisplay() {
      if (this.selections.rangeBasedCompensation) {
        return "Range based compensation";
      }
      if (this.selections.scaleBasedCompensation) {
        return "Scale based compensation";
      }
      return "None selected.";
    },
    reviewRows() {
      return [
        { label: "Organisation type", value: this.organisationType },
        {
          label: "Job classification",
          value: this.reviewJobClassificationDisplay,
        },
        {
          label: "Levels & progression",
          value: this.reviewLevelsDisplay,
        },
        {
          label: "Compensation model",
          value: this.reviewCompensationDisplay,
        },
      ];
    },
    hasStep1Selection() {
      return Object.values(this.selections).some(Boolean);
    },
    isContinueDisabled() {
      if (this.savingOrgSetup) {
        return true;
      }
      if (this.currentStep === 1 && !this.hasStep1Selection) {
        return true;
      }
      return this.currentStep === 2 && !this.reviewConfirmed;
    },
    moduleQueueRows() {
      const payload = this.savedSetupPayload || {};
      const progression = Array.isArray(payload.jobLevelProgression)
        ? payload.jobLevelProgression
        : [];
      const rows = [{ label: "Organization Structure" }];

      const jobClassificationMap = {
        jobCadre: "Job cadres",
        jobFamily: "Job family",
      };
      const classificationLabel = jobClassificationMap[payload.jobClassification];
      if (classificationLabel) {
        rows.push({ label: classificationLabel });
      }

      if (payload.hasSchemeOfService) {
        rows.push({ label: "Scheme of service" });
      }

      const progressionMap = {
        jobLevel: "Job levels",
        jobGrade: "Job grades",
        gradeLevel: "Grade levels",
        steps: "Steps",
      };
      progression.forEach((key) => {
        const progressionLabel = progressionMap[key];
        if (progressionLabel) {
          rows.push({ label: progressionLabel });
        }
      });

      rows.push({ label: "Compensation" });

      rows.push({ label: "Posts" });

      return rows.map((row, idx) => ({
        ...row,
        index: idx + 1,
        status: idx === 0 ? "Up next" : "Pending",
      }));
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.resetOnboardingState();
      }
    },
    jobGradeDisabled(isDisabled) {
      if (this.selections.jobFamily || this.selections.jobCadre) {
        return;
      }
      if (isDisabled) this.selections.jobGrade = false;
    },
    rangeBasedDisabled(isDisabled) {
      if (isDisabled && this.selections.rangeBasedCompensation) {
        this.selections.rangeBasedCompensation = false;
        if (!this.selections.scaleBasedCompensation) {
          this.selections.scaleBasedCompensation = true;
        }
      }
    },
    "selections.gradeLevel"(on) {
      this.selections.steps = !!on;
    },
    currentStep(val, oldVal) {
      if (val === 2 && oldVal === 1) {
        this.reviewConfirmed = false;
      }
    },
  },
  methods: {
    resetOnboardingState() {
      this.currentStep = 1;
      this.reviewConfirmed = false;
      this.savingOrgSetup = false;
      this.savedSetupPayload = null;
      this.selections = {
        jobFamily: false,
        jobCadre: false,
        schemeOfService: false,
        jobLevel: false,
        jobGrade: false,
        gradeLevel: false,
        steps: false,
        rangeBasedCompensation: false,
        scaleBasedCompensation: false,
      };
    },
    onStep1OptionToggle({ key, value }) {
      this.selections[key] = value;
      switch (key) {
        case "jobFamily":
          this.onJobFamilyChange();
          break;
        case "jobCadre":
          this.onJobCadreChange();
          break;
        case "schemeOfService":
          this.onSchemeChange();
          break;
        case "jobLevel":
          this.onJobLevelChange();
          break;
        case "jobGrade":
          this.onJobGradeChange();
          break;
        case "gradeLevel":
          this.onGradeLevelChange();
          break;
        case "steps":
          this.onStepsChange();
          break;
        case "rangeBasedCompensation":
          this.onRangeBasedChange();
          break;
        case "scaleBasedCompensation":
          this.onScaleBasedChange();
          break;
        default:
          break;
      }
    },
    onJobFamilyChange() {
      if (this.selections.jobFamily) {
        this.selections.jobCadre = false;
        this.selections.schemeOfService = false;
        this.selections.jobLevel = true;
        this.selections.jobGrade = true;
        this.selections.gradeLevel = false;
        this.selections.steps = false;
        this.selections.rangeBasedCompensation = true;
        this.selections.scaleBasedCompensation = false;
      } else {
        this.selections.jobLevel = false;
        this.selections.jobGrade = false;
        this.selections.rangeBasedCompensation = false;
        this.selections.scaleBasedCompensation = false;
      }
    },
    onJobCadreChange() {
      if (this.selections.jobCadre) {
        this.selections.jobFamily = false;
        this.selections.jobLevel = false;
        this.selections.jobGrade = false;
        this.selections.gradeLevel = true;
        this.selections.steps = true;
        this.selections.rangeBasedCompensation = false;
        this.selections.scaleBasedCompensation = true;
      } else {
        this.selections.schemeOfService = false;
        this.selections.gradeLevel = false;
        this.selections.steps = false;
      }
    },
    onSchemeChange() {
      if (this.selections.schemeOfService && !this.selections.jobCadre) {
        this.selections.schemeOfService = false;
      }
    },
    onJobLevelChange() {
      if (this.selections.jobLevel) {
        this.selections.gradeLevel = false;
        this.selections.steps = false;
      } else {
        this.selections.rangeBasedCompensation = false;
        this.selections.jobGrade = false;
        if (!this.selections.scaleBasedCompensation) {
          this.selections.scaleBasedCompensation = true;
        }
      }
    },
    onJobGradeChange() {
      if (this.jobGradeDisabled) {
        this.selections.jobGrade = false;
      }
    },
    onGradeLevelChange() {
      if (this.gradeLevelDisabled) {
        this.selections.gradeLevel = false;
        return;
      }
      if (this.selections.gradeLevel) {
        this.selections.jobLevel = false;
        this.selections.jobGrade = false;
        this.selections.rangeBasedCompensation = false;
        if (!this.selections.scaleBasedCompensation) {
          this.selections.scaleBasedCompensation = true;
        }
      } else {
        this.selections.steps = false;
      }
    },
    onStepsChange() {
      this.selections.steps = !!this.selections.gradeLevel;
    },
    onRangeBasedChange() {
      if (this.rangeBasedDisabled) {
        this.selections.rangeBasedCompensation = false;
        return;
      }
      if (this.selections.rangeBasedCompensation) {
        this.selections.scaleBasedCompensation = false;
      } else {
        if (!this.selections.jobFamily) {
          this.selections.jobGrade = false;
        }
        if (!this.selections.scaleBasedCompensation) {
          this.selections.scaleBasedCompensation = true;
        }
      }
    },
    onScaleBasedChange() {
      if (this.selections.scaleBasedCompensation) {
        this.selections.rangeBasedCompensation = false;
        if (!this.selections.jobFamily) {
          this.selections.jobGrade = false;
        }
      } else if (!this.selections.rangeBasedCompensation) {
        if (this.selections.jobLevel) {
          this.selections.rangeBasedCompensation = true;
        } else {
          this.selections.scaleBasedCompensation = true;
        }
      }
    },
    buildOrgSetupPayload() {
      const s = this.selections;
      const jobClassification = s.jobCadre ? "jobCadre" : "jobFamily";
      const jobLevelProgression = [];
      if (s.jobLevel) jobLevelProgression.push("jobLevel");
      if (s.jobGrade) jobLevelProgression.push("jobGrade");
      if (s.gradeLevel) jobLevelProgression.push("gradeLevel");
      if (s.steps) jobLevelProgression.push("steps");
      const compensationModel = s.rangeBasedCompensation
        ? "rangeBased"
        : "scaleBased";
      return {
        jobClassification,
        hasSchemeOfService: !!s.schemeOfService,
        jobLevelProgression,
        compensationModel,
      };
    },
    async persistOrgSetup() {
      const payload = this.buildOrgSetupPayload();
      await this.$_saveOrgSetup(payload);
      this.savedSetupPayload = payload;
    },
    async handlePrimaryStep() {
      if (this.currentStep === 1 && !this.hasStep1Selection) {
        return;
      }
      if (this.currentStep === 2 && !this.reviewConfirmed) {
        this.$toasted.error(
          "Please confirm your selections before continuing.",
          { duration: 4000 }
        );
        return;
      }
      if (this.currentStep === 1) {
        this.currentStep = 2;
        return;
      }
      if (this.currentStep === 2) {
        this.savingOrgSetup = true;
        try {
          await this.persistOrgSetup();
          this.$toasted.success("Organisation setup saved", {
            duration: 4000,
          });
          this.currentStep = 3;
        } catch (error) {
          this.$toasted.error("Unable to save organisation setup", {
            duration: 5000,
          });
        } finally {
          this.savingOrgSetup = false;
        }
      }
    },
    goToAdminDashboard() {
      this.$router.push({ name: 'AdminDashboard' })
    },
    finishOnboarding() {
      this.$emit("setup-saved");
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.org-onboarding-modal {
  border-radius: 14px;
}

.step-tab {
  position: relative;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 9999px;
}

.step-circle--active {
  background: var(--org-onboarding-accent, #FF5E3A);
}

.step-circle--inactive {
  background: #fff;
  border: 1px solid #d1d5db;
}

.step-underline--active {
  background: var(--org-onboarding-accent, #FF5E3A);
}
</style>
